<template>
  <v-combobox
    :id="snInputProps_identifier"
    ref="vCombobox"
    v-model="response"
    :class="snInputProps_classes"
    :data-cy="snInputProps_placeholder + '-dropdown'"
    :placeholder="snInputProps_disabled ? null : snInputProps_placeholder"
    :autofocus="snInputProps_autofocus"
    :disabled="snInputProps_disabled"
    :error-messages="snInputProps_errorMessages"
    :aria-label="snInputProps_label"
    :hide-details="snInputProps_hideDetails && (snInputProps_errorMessages == null || snInputProps_errorMessages.length === 0)"
    :items="options"
    :attach="snInputProps_attach"
    item-color="snPrimary"
    loader-height="100"
    autocomplete="nope"
    :append-icon="appendIcon"
    outlined
    auto
    :chips="chips"
    :multiple="multiple"
    :rules="snInputProps_rules"
    :validate-on-blur="snInputProps_validateOnBlur"
    :type="snInputProps_type"
    @keyup.native.enter="$emit('keyup.native.enter')"
    @change="onChange()"
    @blur="$emit('blur')"
    @click.native="$emit('click.native')"
    @update:search-input="$emit('search-input', $event)"
  >
    <template
      v-if="chips"
      v-slot:selection="{item, parent}"
    >
      <v-chip
        :key="JSON.stringify(item)"
        :close="close"
        close-icon="sn-icon-exit"
        color="snGray01"
        @click:close="parent.selectItem(item)"
      >
        <span class="sn-text-primary">{{ item }}</span>
      </v-chip>
    </template>
  </v-combobox>
</template>

<script>

import SnInputProps from '../../props/SnInputProps'
import deprecatedMixin from '~/components/snui/v2/_deprecated/deprecatedMixin.vue'

export default {
  name: 'SnComboboxDeprecated',
  mixins: [deprecatedMixin],
  props: {
    // v-combobox :rules get applied against the entire v-model array when multiple === true
    // (v) => v.every( el => validation code goes here ) || "error message"
    ...SnInputProps,
    value: {
      type: [String, Number, Array],
      default: '',
      required: false
    },
    options: {
      type: Array,
      required: false,
      default () {
        return []
      }
    },
    chips: {
      type: Boolean,
      required: false,
      default: false
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    close: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      response: this.value,
      deprecatedMixin: {
        alternativeComponent: 'SnCombobox'
      }
    }
  },
  computed: {
    appendIcon () {
      return this.options.length ? 'sn-icon-chevron-down-small' : ''
    }
  },
  watch: {
    value (newVal) {
      this.response = newVal
    },
    response (newVal) {
      this.$emit('input', this.response)
    }
  },
  methods: {
    onChange () {
      this.$nextTick(() => {
        this.$refs.vCombobox.isMenuActive = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui/index.scss';

.v-input--is-disabled {
  ::v-deep .v-input__slot {
    background-color: $sn-gray-01 !important;
    color: $sn-primary-01;
    fieldset {
      border: 1px solid $sn-gray-04;
    }
  }
}

::v-deep .v-input--is-disabled {
  .v-input__append-inner {
    display: none;
  }
}

::v-deep {
  font-family: $font-family-primary !important;

  .v-select__selections {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  input::placeholder {
    color: $sn-primary-01 !important;
  }

  .v-input__slot {
    background-color: $sn-white !important;
    padding: 0 12px !important;
    box-sizing: border-box;
    border-radius: 2px !important;
    min-height: 48px !important;
    cursor: pointer !important;

    fieldset {
      color: rgba(0,0,0,.38);
      border: 1px solid !important;
    }

    &:hover, &:focus {
      fieldset {
        color: rgba(0, 0, 0, .86);
      }
    }

    input {
      font-size: 16px !important;
      cursor: pointer !important;
    }

    .v-input__append-inner {
      margin-top: 12px !important;
    }

    .v-icon {
      color: $sn-primary !important;
    }
  }

  .v-text-field__details {
    padding: 0 !important;
    margin-bottom: 0 !important;
  }

  .v-messages {
    color: $sn-primary-01;

    .v-messages__message {
      font-size: 14px;
      line-height: 22px;
      margin-bottom: 0;
    }

    &.error--text {
      font-size: 15px;
      display: inline-flex;
      align-items: center;
      visibility: visible;

      &:before {
        content: '\26A0';
        margin-right: 5px;
        font-size: 15px;
        margin-bottom: 0;
      }

      .v-messages__wrapper {
        display: inline-flex;
        padding-bottom: 2px;
      }
    }
  }

  .error--text {
    fieldset {
      color: #ff5252 !important;
    }
  }

  .v-list-item {
    padding: 0 16px;
  }

  .v-list-item__content {
    height: 40px;
    padding: 8px 0;

    .v-list-item__title {
      font-size: 17px;
      font-weight: $font-weight-regular;
      line-height: 24px;
    }
  }
}

.v-autocomplete__content.v-menu__content .v-select-list {
  margin: 8px 0 !important;
}

::v-deep .v-list-item--active {
  background-color: $sn-brand-01 !important;

  &:before {
    opacity: 0;
  }
}

::v-deep .v-list-item--link:hover {
  background-color: $sn-brand-01 !important;

  &:before {
    opacity: 0;
  }
}

::v-deep .v-list {
  padding: 0 !important;
}

::v-deep .v-menu__content {
  z-index: 100 !important;
  box-shadow: $sn-shadow-depth-3;
}

/* Chrome, Safari, Edge, Opera */
::v-deep input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
::v-deep input[type=number] {
  -moz-appearance: textfield;
}

</style>
